import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { RegEdComplianceContentType } from './types';

export type RegEdOfficerReviewContentType =
	| RegEdComplianceContentType.Email
	| RegEdComplianceContentType.Blog
	| RegEdComplianceContentType.SocialMediaPost;

const SEND_COMPLIANCE_PATH: Record<RegEdOfficerReviewContentType, string> = {
	[RegEdComplianceContentType.Email]: 'email',
	[RegEdComplianceContentType.Blog]: 'blog',
	[RegEdComplianceContentType.SocialMediaPost]: 'social/post',
};

/**
 * Emails the compliance officer a fresh review link for a piece held on them. The same `sendCompliance`
 * route as the legacy PDF copy: on an officer-filing account the API sends the portal link instead, always to
 * the account's compliance address, so no recipient is passed.
 */
export const useRegEdOfficerReviewLinkResendMutation = ({
	onError,
	onSuccess,
}: {
	onError?: (error: Api.IOperationResultNoValue) => void;
	onSuccess?: () => void;
} = {}) => {
	const userSession = useUserSession();
	return useMutation({
		mutationFn: async ({
			contentType,
			parentId,
			impersonationContext,
		}: {
			contentType: RegEdOfficerReviewContentType;
			parentId: string;
			impersonationContext?: Api.IImpersonationContext;
		}) => {
			await userSession.webServiceHelper.callAsync(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: `${SEND_COMPLIANCE_PATH[contentType]}/${encodeURIComponent(parentId)}/sendCompliance`,
				}),
				'POST',
				null
			);
		},
		onError,
		onSuccess,
	});
};
